import React, { FC } from "react"
import { graphql } from "gatsby"

import { PostTemplateQueryQuery } from "@graphqlTypes"

// Shared Components
import NewsletterPopIn from "@components/NewsletterPopIn/NewsletterPopIn"

// Posts Components
import PostsNav from "@templates/posts/components/PostsNav/PostsNav"

// Page Components
import PostContent from "../components/PostContent/PostContent"
import PostFooter, { POST_FOOTER_STYLES } from "../components/PostFooter/PostFooter"
import PostHead from "../components/PostHead/PostHead"
import PostStructuredData from "../components/PostStructuredData/PostStructuredData"

// Heroes
import PostHeroCustomerStory from "./components/heroes/PostHeroCustomerStory/PostHeroCustomerStory"
import PostHeroBlog from "./components/heroes/PostHeroBlog/PostHeroBlog"
import PostHeroWebinar from "./components/heroes/PostHeroWebinar/PostHeroWebinar"

const POST_VARIANTS = {
  BLOG: "BLOG",
  GUIDE: "GUIDE",
  WEBINAR: "WEBINAR",
  CUSTOMER_STORY: "CUSTOMER_STORY",
}

export const Head = PostHead

const Post: FC<{ data: PostTemplateQueryQuery }> = ({
  data: {
    contentfulPost,
    site,
  },
}) => {
  if (!contentfulPost) return null

  const siteUrl = site?.siteMetadata?.siteUrl || ""

  const {
    authors,
    category,
    content,
    date,
    dateModified,
    description,
    extendedImage,
    heroColor,
    heroCompanySize,
    heroFeatures,
    heroIndustry,
    hubspotFormData,
    image,
    imageArtist,
    imageBackgroundColor,
    permalink,
    prefooter,
    prefooterPdfLink,
    series,
    thumbnailImage,
    title,
    updatedAt,
    useExtendedImage = false,
    variant,
    wistiaId,
    wistiaPreviewImage,
    wistiaVideoLength,
  } = contentfulPost
  return (
    <>
      <PostStructuredData
        authors={authors}
        category={category}
        date={date || updatedAt}
        dateModified={dateModified || updatedAt}
        description={description && description.description}
        imageUrl={
          thumbnailImage
            ? thumbnailImage?.url
            : image?.url
        }
        permalink={permalink}
        siteUrl={siteUrl}
        title={title}
      />
      <article style={{ marginTop: 'var(--SiteNav--height)' }}>
        <PostsNav currentCategorySlug={category && category[0]?.slug} />
        {/* HERO SECTION */}
        {(!variant || variant === POST_VARIANTS.BLOG) && (
          <PostHeroBlog
            authors={authors}
            dateModified={dateModified || updatedAt}
            image={useExtendedImage ? extendedImage : image}
            imageArtist={imageArtist}
            imageBackgroundColor={imageBackgroundColor}
            title={title}
            useExtendedImage={useExtendedImage}
          />
        )}
        {variant === POST_VARIANTS.WEBINAR && (
          <PostHeroWebinar
            authors={authors}
            dateModified={dateModified || updatedAt}
            description={description}
            heroColor={heroColor}
            hubspotFormData={hubspotFormData}
            permalink={permalink}
            series={series}
            siteUrl={siteUrl}
            title={title}
            wistiaId={wistiaId}
            wistiaPreviewImage={wistiaPreviewImage || image || thumbnailImage}
            wistiaVideoLength={wistiaVideoLength}
          />
        )}

        {variant === POST_VARIANTS.CUSTOMER_STORY && (
          <PostHeroCustomerStory
            authors={authors}
            date={date}
            heroCompanySize={heroCompanySize}
            heroFeatures={heroFeatures}
            heroIndustry={heroIndustry}
            image={image}
            title={title}
          />
        )}
        <PostContent
          authors={authors}
          content={content}
          fullUrl={`${siteUrl}${permalink}`}
          hideSocialShare={variant === POST_VARIANTS.WEBINAR}
          title={title || ""}
        />
        {/* CALLOUTS */}
        <PostFooter
          footerStyle={prefooter as POST_FOOTER_STYLES}
          pdfLink={prefooterPdfLink}
          title={title}
        />
        <NewsletterPopIn />
      </article>
    </>
  )
}

export const pageQuery = graphql`
  fragment PostContentReferencesFragment on ContentfulAssetContentfulContentBlockBlockquoteContentfulContentBlockCalloutContentfulContentBlockImagesContentfulContentBlockRelatedContentContentfulContentBlockTableContentfulContentBlockTextWithImageContentfulContentBlockVideoContentfulCtaContentfulLpAccordionBlockContentfulLpBespokeComponentContentfulPostUnion {
    __typename
    ... on ContentfulAsset {
      ...ContentImagesImageFragment
    }
    ... on ContentfulPost {
      ...CardFragment
    }
    ... on ContentfulContentBlockBlockquote {
      ...ContentBlockquoteFragment
    }
    ... on ContentfulContentBlockCallout {
      ...ContentCalloutFragment
    }
    ... on ContentfulContentBlockImages {
      ...ContentImagesFragment
    }
    ... on ContentfulContentBlockRelatedContent {
      ...RelatedContentFragment
    }
    ... on ContentfulContentBlockTextWithImage {
      ...TextWithImageFragment
    }
    ... on ContentfulContentBlockVideo {
      ...VideoFragment
    }
    ... on ContentfulCta {
      ...CtaFragment
    }
    ... on ContentfulContentBlockTable {
      ...TableFragment
    }
    ... on ContentfulLpAccordionBlock {
      ...AccordionBlockFragment
    }
    ... on ContentfulLpBespokeComponent {
      ...BespokeComponentFragment
    }
  }

  fragment PostTemplateAuthorFragment on ContentfulPerson {
    ...AvatarFragment
    bio {
      childMarkdownRemark {
        html
      }
    }
    jobTitle
    permalink
    twitter
    url
  }

  fragment PostTemplatePostFragment on ContentfulPost {
    authors {
      ...PostTemplateAuthorFragment
    }
    category {
      slug
      permalink
      title
    }
    date
    dateModified
    updatedAt
    description {
      description
    }
    heroColor
    heroQuote {
      heroQuote
    }
    heroIndustry
    heroFeatures
    heroCompanySize
    hubspotFormData {
      ...HubspotFormFragment
    }
    imageBackgroundColor
    image {
      title
      url
      imgixImage {
        gatsbyImageData
        url
      }
    }
    thumbnailImage {
      url
      imgixImage {
        url
      }
    }
    extendedImage: image {
      imgixImage {
        gatsbyImageData(width: 1323)
      }
    }
    imageArtist
    permalink
    prefooter
    prefooterPdfLink
    seoNoindex
    seoTitle
    series {
      title
    }
    title
    useExtendedImage
    variant
    wistiaId
    wistiaPreviewImage {
      imgixImage {
        gatsbyImageData(width: 800)
      }
    }
    wistiaVideoLength
    content {
      raw
      references {
        ...PostContentReferencesFragment
      }
    }
  }

  query PostTemplateQuery(
    $id: String!
  ) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    contentfulPost(
      id: { eq: $id }
    ) {
      ...PostTemplatePostFragment
    }
  }
`

export default Post